import type { TableFilter } from '@parcel/filters';
import {
  Button,
  DeleteViewDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Skeleton,
  ViewOptionsMenu,
  ViewTabs,
  WORKSPACE_VIEW_DELETE_DESCRIPTION,
} from '@parcel/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useRouterState } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import { Loader2, SlidersHorizontal } from 'lucide-react';
import * as React from 'react';
import { BrowseSummaryMeta } from '../components/data-table/browse-summary-meta.js';
import { ViewSelectionToolbar } from '../components/data-table/view-selection-toolbar.js';
import { PageHeader } from '../components/page-header.js';
import {
  type OpenProjectDetail,
  ProjectsDataTable,
} from '../components/projects/projects-data-table.js';
import {
  PROJECTS_DEFAULT_SORTING,
  useProjectsTable,
} from '../components/projects/use-projects-table.js';
import { useAuth } from '../lib/auth.js';
import { BROWSE_PAGE_SIZE } from '../lib/browse-query.js';
import {
  useEntityBrowseFirstRowsTiming,
  useEntityBrowseLaterPageSignals,
} from '../lib/entity-browse-observability.js';
import { filterEntriesKey } from '../lib/filter-params.js';
import { useAskFilters } from '../lib/filters-ask.js';
import { listTransitionKey } from '../lib/list-transition.js';
import {
  type ApiProjectMapSummary,
  projectMapTileTemplate,
  useProjectMapSummary,
} from '../lib/project-map.js';
import type { ProjectRow } from '../lib/projects-display.js';
import { apiProjectToRow, useProjectSearchPreview, useWorkspaceProjects } from '../lib/projects.js';
import { useRecordDetail } from '../lib/record-detail.js';
import {
  DRAFT_VIEW_ID,
  DRAFT_VIEW_NAME,
  createDraftSavedView,
  isConfirmedViewName,
} from '../lib/saved-view-drafts.js';
import { useScore } from '../lib/scores.js';
import { isSupabaseConfigured } from '../lib/supabase.js';
import {
  applyAskWhere,
  hasTableFilter,
  makeTableSearchValidator,
  tableFilterEntries,
  useTableFilterSearch,
  useTableViewSearchParam,
} from '../lib/table-filter.js';
import { PROJECTS_BASE_PRESENTATION } from '../lib/table-views.js';
import {
  useEntityListViewScope,
  useWorkspaceTableViews,
} from '../lib/use-workspace-table-views.js';
import { runChunkedMembership, selectionMap } from '../lib/view-selection-state.js';
import { useWatchSelection } from '../lib/watch-selection.js';
import { useWhereOptions } from '../lib/where-options.js';
import {
  type WorkspaceView,
  useFavoriteViewMutations,
  useFavoriteViews,
  useWorkspaceViewMutations,
  useWorkspaceViews,
} from '../lib/workspace-views.js';
import { getWorkspace, getWorkspaceSlugFromPath } from '../lib/workspaces.js';

const ProjectsMap = React.lazy(() =>
  import('../components/projects/projects-map.js').then((m) => ({ default: m.ProjectsMap })),
);

const SEARCH_DEBOUNCE_MS = 250;

export const Route = createFileRoute('/projects')({
  component: ProjectsPage,
  validateSearch: makeTableSearchValidator({ view: 'uuid' }),
});

export function ProjectsPage() {
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { session } = useAuth();
  const userId = session?.user.id;
  const workspaceSlug = getWorkspaceSlugFromPath(pathname);
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    enabled: Boolean(workspaceSlug) && isSupabaseConfigured,
    queryFn: () => getWorkspace(workspaceSlug ?? ''),
  });
  const workspaceId = workspaceQuery.data?.id;

  const { filter: filterState, setFilter: setFilterState } = useTableFilterSearch('project');
  const { activeViewId: urlViewId, setActiveViewId, urlHasFilterKeys } = useTableViewSearchParam();
  const filterEntries = React.useMemo(() => tableFilterEntries(filterState), [filterState]);
  // Mirror filterState so the async ask handler merges into the latest state.
  const filterStateRef = React.useRef(filterState);
  filterStateRef.current = filterState;
  const { mutateAsync: askFilters } = useAskFilters();
  const handleAsk = React.useCallback(
    async (query: string) => {
      if (!workspaceId) throw new Error('workspace not loaded');
      const result = await askFilters({ workspaceId, entity: 'project', query });
      setFilterState(applyAskWhere(filterStateRef.current, result));
      return result;
    },
    [askFilters, workspaceId, setFilterState],
  );

  const viewsQuery = useWorkspaceViews(workspaceId, 'project');
  const viewsListStatus = viewsQuery.isPending
    ? 'pending'
    : viewsQuery.isError
      ? 'error'
      : 'success';
  const listScope = useEntityListViewScope({
    views: viewsQuery.data,
    urlViewId,
    dataset: 'project',
    listStatus: viewsListStatus,
    urlHasFilterKeys,
    filterState,
  });
  const browseMode = true;
  const [sorting, setSorting] = React.useState<SortingState>(PROJECTS_DEFAULT_SORTING);
  const workspacePending =
    isSupabaseConfigured && Boolean(workspaceSlug) && workspaceQuery.isPending;
  const mapScopeKey = React.useMemo(
    () =>
      `${workspaceId ?? ''}|${urlViewId ?? ''}|${filterEntriesKey(filterEntries)}|${listScope.staticViewId ?? ''}`,
    [filterEntries, listScope.staticViewId, urlViewId, workspaceId],
  );
  const mapSummaryQuery = useProjectMapSummary(workspaceId, filterEntries, listScope.staticViewId, {
    enabled: listScope.ready,
  });
  const [acceptedMap, setAcceptedMap] = React.useState<{
    workspaceId: string;
    scopeKey: string;
    summary: ApiProjectMapSummary;
  } | null>(null);
  React.useEffect(() => {
    setAcceptedMap((current) => {
      if (!current || current.workspaceId === workspaceId) return current;
      return null;
    });
  }, [workspaceId]);
  React.useEffect(() => {
    if (!workspaceId || !listScope.ready || !mapSummaryQuery.data) return;
    setAcceptedMap((current) => {
      if (current?.scopeKey === mapScopeKey) return current;
      return { workspaceId, scopeKey: mapScopeKey, summary: mapSummaryQuery.data };
    });
  }, [listScope.ready, mapScopeKey, mapSummaryQuery.data, workspaceId]);
  const acceptedSummary =
    listScope.ready && acceptedMap?.scopeKey === mapScopeKey ? acceptedMap.summary : undefined;

  const projectsQuery = useWorkspaceProjects(workspaceId, filterEntries, listScope.staticViewId, {
    enabled: listScope.ready,
    browseMode,
    sorting,
  });

  // Identity of the active view + filters. A view/filter change navigates the
  // URL, and the list query only re-keys once that navigation commits (a few
  // hundred ms later); until then the old rows sit on screen with no loading
  // state. Flip a flag synchronously on the change so the skeleton shows
  // immediately, then clear it once this identity moves off the pre-change value
  // AND the query settles. The view id MUST be part of the identity: two views
  // can share a filter (e.g. Architects vs Architects New), and without it a
  // switch between them never registers as changed and the skeleton sticks.
  const listKey = listTransitionKey(urlViewId, filterEntriesKey(filterEntries));
  const [isListTransitioning, setIsListTransitioning] = React.useState(false);
  const transitionFromKeyRef = React.useRef(listKey);
  const beginListTransition = React.useCallback(() => {
    transitionFromKeyRef.current = listKey;
    setIsListTransitioning(true);
  }, [listKey]);
  React.useEffect(() => {
    if (
      isListTransitioning &&
      listKey !== transitionFromKeyRef.current &&
      !projectsQuery.isFetching &&
      !projectsQuery.isPlaceholderData
    ) {
      setIsListTransitioning(false);
    }
  }, [isListTransitioning, listKey, projectsQuery.isFetching, projectsQuery.isPlaceholderData]);
  const handleFilterStateChange = React.useCallback(
    (next: TableFilter) => {
      // A refused or unchanged filter never moves the list, so it starts no loading state.
      if (setFilterState(next)) beginListTransition();
    },
    [beginListTransition, setFilterState],
  );
  const atlasScore = useScore(workspaceId, 'atlas').data;
  const whereOptions = useWhereOptions(workspaceId, 'project');

  const [recordInput, setRecordInput] = React.useState('');
  const [debouncedRecordQuery, setDebouncedRecordQuery] = React.useState('');
  React.useEffect(() => {
    const handle = window.setTimeout(
      () => setDebouncedRecordQuery(recordInput.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(handle);
  }, [recordInput]);
  const previewQuery = useProjectSearchPreview(
    workspaceId,
    debouncedRecordQuery,
    filterEntries,
    listScope.staticViewId,
  );
  const previewRecords = React.useMemo(
    () =>
      (previewQuery.data?.projects ?? []).map((project) => ({
        id: project.id,
        label: project.name ?? project.primary_address ?? 'Unnamed project',
        sublabel: [project.city, project.primary_address].filter(Boolean).join(' · '),
      })),
    [previewQuery.data],
  );

  const projects = React.useMemo<ProjectRow[]>(
    () => (projectsQuery.data?.projects ?? []).map(apiProjectToRow),
    [projectsQuery.data],
  );
  // Server-side filtering means `projects` is already the filtered set — the
  // map stays in sync with the table without a client-side filtered-rows pass.
  const { table } = useProjectsTable(projects, {
    browseMode,
    sorting,
    onSortingChange: setSorting,
    atlasScore,
  });
  const { record, open: openRecord, close: closeRecord } = useRecordDetail();
  const openProjectId = record?.type === 'project' ? record.id : null;
  // A null open only comes from the table dropping the row on a scope change: keep that scope.
  const handleOpenProject = React.useCallback(
    (project: OpenProjectDetail | null) => {
      if (project) {
        openRecord(
          { type: 'project', id: project.id },
          { seed: project.seed, source: project.source },
        );
      } else closeRecord({ unwind: false });
    },
    [closeRecord, openRecord],
  );
  const [browseRefreshRevision, setBrowseRefreshRevision] = React.useState(0);
  const tableScopeKey = React.useMemo(
    () =>
      `${workspaceId ?? ''}|${urlViewId ?? ''}|${filterEntriesKey(filterEntries)}|${listScope.staticViewId ?? ''}|${sorting.map((sort) => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`).join(',')}|${browseRefreshRevision}`,
    [browseRefreshRevision, filterEntries, listScope.staticViewId, sorting, urlViewId, workspaceId],
  );

  useEntityBrowseFirstRowsTiming(
    'project',
    browseMode,
    tableScopeKey,
    projects.length,
    projectsQuery.isLoading,
  );
  const loadedPageCount = projects.length === 0 ? 0 : Math.ceil(projects.length / BROWSE_PAGE_SIZE);
  const { wrapFetchNextPage } = useEntityBrowseLaterPageSignals(
    'project',
    browseMode,
    projectsQuery.isFetchNextPageError ?? false,
    loadedPageCount,
  );
  const instrumentedFetchNextPage = React.useMemo(
    () => wrapFetchNextPage(projectsQuery.fetchNextPage ?? (async () => undefined)),
    [projectsQuery.fetchNextPage, wrapFetchNextPage],
  );

  const handleBrowseRefresh = React.useCallback(() => {
    queryClient.removeQueries({
      queryKey: [
        'projects-browse',
        workspaceId,
        filterEntriesKey(filterEntries),
        listScope.staticViewId ?? null,
      ],
    });
    table.resetRowSelection();
    setBrowseRefreshRevision((revision) => revision + 1);
    if (workspaceId && mapSummaryQuery.data) {
      setAcceptedMap({ workspaceId, scopeKey: mapScopeKey, summary: mapSummaryQuery.data });
    }
    void mapSummaryQuery.refetch();
  }, [
    filterEntries,
    listScope.staticViewId,
    mapScopeKey,
    mapSummaryQuery,
    queryClient,
    table,
    workspaceId,
  ]);

  const savedViews = useWorkspaceTableViews({
    workspaceId,
    dataset: 'project',
    table,
    basePresentation: PROJECTS_BASE_PRESENTATION,
    filterState,
    setFilterState,
    activeViewId: urlViewId,
    setActiveViewId,
    urlHasFilterKeys,
  });
  const favoritesQuery = useFavoriteViews(workspaceId, userId);
  const favoriteMutations = useFavoriteViewMutations(workspaceId, userId);
  const activeViewIsFavorite = Boolean(
    savedViews.activeView &&
      (favoritesQuery.data?.some((view) => view.id === savedViews.activeView?.id) ??
        savedViews.activeView.is_favorite),
  );
  const toggleFavorite = (view: WorkspaceView) => {
    const isFavorite =
      favoritesQuery.data?.some((favorite) => favorite.id === view.id) ?? Boolean(view.is_favorite);
    if (isFavorite) favoriteMutations.unfavorite.mutate(view.id);
    else favoriteMutations.favorite.mutate({ view_id: view.id });
  };
  const [editingViewId, setEditingViewId] = React.useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [editingValue, setEditingValue] = React.useState('');
  const [draftView, setDraftView] = React.useState<ReturnType<typeof createDraftSavedView> | null>(
    null,
  );
  const visibleViews = [
    ...savedViews.views.map((view) => ({
      id: view.id,
      name: view.name,
      // The system Following tabs say so in their name; only a followed list View gets the star.
      followed: view.watched && !view.system_kind,
    })),
    ...(draftView ? [{ id: draftView.id, name: draftView.name }] : []),
  ];
  const activeViewId = draftView ? draftView.id : savedViews.activeViewId;
  const isViewTabsDirty = draftView ? false : savedViews.canSaveAsNew;
  // Skeleton on: initial load, the keepPreviousData fetch window, AND the
  // click->navigation gap before the query re-keys (isListTransitioning).
  const isListLoading =
    projectsQuery.isLoading || projectsQuery.isPlaceholderData || isListTransitioning;

  const startNewView = () => {
    const view = createDraftSavedView({
      columnFilters: [],
      sorting: [],
      columnVisibility: {},
      globalFilter: '',
    });
    setDraftView(view);
    setEditingViewId(DRAFT_VIEW_ID);
    setEditingValue(DRAFT_VIEW_NAME);
  };

  const finishEditingView = () => {
    const nextName = editingValue.trim();
    if (draftView && editingViewId === DRAFT_VIEW_ID) {
      if (isConfirmedViewName(nextName)) {
        void savedViews.saveAsNew(nextName);
      }

      setDraftView(null);
      setEditingViewId(null);
      setEditingValue('');
      return;
    }

    if (editingViewId !== savedViews.activeViewId) {
      setEditingViewId(null);
      setEditingValue('');
      return;
    }

    if (nextName) {
      void savedViews.renameView(nextName);
    }

    setEditingViewId(null);
    setEditingValue('');
  };

  const cancelEditingView = () => {
    setDraftView(null);
    setEditingViewId(null);
    setEditingValue('');
  };

  const applyBaseView = () => {
    if (urlViewId !== null || hasTableFilter(filterState)) beginListTransition();
    setDraftView(null);
    savedViews.applyBaseView();
  };

  const applySavedView = (viewId: string) => {
    if (viewId !== urlViewId) beginListTransition();
    setDraftView(null);
    savedViews.applyView(viewId);
  };

  const viewMutations = useWorkspaceViewMutations(workspaceId, 'project');
  const followSelection = useWatchSelection(workspaceId, 'project');
  const activeView = savedViews.activeView;
  const toggleWatch =
    activeView?.view_type === 'static' && !activeView.system_kind
      ? () => viewMutations.upsert.mutate({ id: activeView.id, watched: !activeView.watched })
      : undefined;
  const selectedIds = table.getSelectedRowModel().rows.map((row) => row.original.id);
  const staticViews = React.useMemo(
    () => savedViews.views.filter((view) => view.view_type === 'static'),
    [savedViews.views],
  );
  const activeStaticView =
    savedViews.activeView?.view_type === 'static' ? savedViews.activeView : null;

  return (
    <main className="flex min-h-0 min-w-0 flex-1 flex-col">
      <PageHeader
        title="Projects"
        meta={
          !isSupabaseConfigured ? null : workspacePending ? (
            <Skeleton className="inline-block h-4 w-24 align-middle" />
          ) : (
            <BrowseSummaryMeta
              summaryQuery={mapSummaryQuery}
              baselineDataVersion={acceptedSummary?.map_version ?? null}
              onRefresh={handleBrowseRefresh}
            />
          )
        }
      />

      <ViewTabs
        views={visibleViews}
        activeViewId={activeViewId}
        isDirty={isViewTabsDirty}
        editingViewId={editingViewId}
        editingValue={editingValue}
        onEditingValueChange={setEditingValue}
        onCommitEditingView={finishEditingView}
        onCancelEditingView={cancelEditingView}
        onSelectBase={applyBaseView}
        onSelectView={applySavedView}
        onSaveAsNew={startNewView}
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-2 rounded-full">
                <SlidersHorizontal className="h-4 w-4" />
                <span className="inline-flex items-center gap-2">
                  Views
                  {savedViews.activeView && savedViews.isDirty ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-destructive" aria-hidden />
                  ) : null}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56"
              onCloseAutoFocus={(event) => event.preventDefault()}
            >
              <ViewOptionsMenu
                hasActiveView={Boolean(savedViews.activeView)}
                isDirty={savedViews.isDirty}
                canSaveAsNew={savedViews.canSaveAsNew}
                onSave={savedViews.saveCurrent}
                onSaveAsNew={startNewView}
                isFavorite={activeViewIsFavorite}
                onToggleFavorite={() => {
                  const view = savedViews.activeView;
                  if (view) toggleFavorite(view);
                }}
                onRename={() => {
                  if (!savedViews.activeView) return;
                  setDraftView(null);
                  setEditingViewId(savedViews.activeView.id);
                  setEditingValue(savedViews.activeView.name);
                }}
                onDelete={() => setDeleteDialogOpen(true)}
                isWatched={Boolean(activeView?.watched)}
                onToggleWatch={toggleWatch}
                systemView={Boolean(activeView?.system_kind)}
                followUnavailable={
                  activeView && activeView.view_type !== 'static'
                    ? 'Only list views can be followed for now'
                    : undefined
                }
              />
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />

      {workspacePending ? (
        <ProjectMapLoadingSurface />
      ) : !listScope.ready ? (
        <ProjectMapLoadingSurface />
      ) : acceptedSummary && workspaceId ? (
        <div className="relative">
          <React.Suspense fallback={<ProjectMapLoadingSurface />}>
            <ProjectsMap
              key={`${mapScopeKey}|${acceptedSummary.map_version}`}
              summary={acceptedSummary}
              tileTemplate={projectMapTileTemplate({
                workspaceId,
                filter: filterEntries,
                staticViewId: listScope.staticViewId,
                mapVersion: acceptedSummary.map_version,
              })}
              accessToken={session?.access_token}
              workspaceId={workspaceId}
              filter={filterEntries}
              staticViewId={listScope.staticViewId}
              onProjectClick={(id) => handleOpenProject({ id, seed: null, source: 'map' })}
            />
          </React.Suspense>
        </div>
      ) : mapSummaryQuery.isError ? (
        <div className="flex h-96 w-full flex-col items-center justify-center gap-3 border-b bg-muted/30 text-sm text-muted-foreground">
          <span>Unable to load the project map.</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void mapSummaryQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <ProjectMapLoadingSurface />
      )}
      <ProjectsDataTable
        table={table}
        browseMode={browseMode}
        scopeKey={tableScopeKey}
        filterState={filterState}
        onFilterStateChange={handleFilterStateChange}
        options={whereOptions}
        openProjectId={openProjectId}
        onOpenProject={handleOpenProject}
        isLoading={isSupabaseConfigured && isListLoading}
        isError={isSupabaseConfigured && projectsQuery.isError}
        hasNextPage={projectsQuery.hasNextPage ?? false}
        isFetchingNextPage={projectsQuery.isFetchingNextPage ?? false}
        isFetchNextPageError={projectsQuery.isFetchNextPageError ?? false}
        fetchNextPage={instrumentedFetchNextPage}
        summaryTotal={acceptedSummary?.total ?? mapSummaryQuery.data?.total ?? null}
        recordMatches={previewRecords}
        onRecordInputChange={(text) => setRecordInput(text)}
        onSelectRecord={(id) => handleOpenProject({ id, seed: null, source: 'search' })}
        onAsk={workspaceId ? handleAsk : undefined}
        selectionActive={selectedIds.length > 0}
        selectionToolbar={
          <ViewSelectionToolbar
            dataset="project"
            selectedIds={selectedIds}
            staticViews={staticViews}
            activeStaticView={activeStaticView}
            onAdd={(viewId) =>
              runChunkedMembership(selectedIds, (chunk) =>
                viewMutations.addRecords.mutateAsync({ viewId, recordIds: chunk }),
              )
            }
            onCreate={async (name) => {
              const created = await viewMutations.staticCreate.mutateAsync({
                name,
                dataset: 'project',
                initial_record_ids: selectedIds.slice(0, 2000),
              });
              return { view: created.view, outcomes: created.results };
            }}
            onRemove={
              activeStaticView
                ? () =>
                    runChunkedMembership(selectedIds, (chunk) =>
                      viewMutations.removeRecords.mutateAsync({
                        viewId: activeStaticView.id,
                        recordIds: chunk,
                      }),
                    )
                : undefined
            }
            onFollow={() => followSelection(selectedIds)}
            onOpenView={(view) => {
              setDraftView(null);
              savedViews.applyView(view.id);
            }}
            onSelectionChange={(ids) => table.setRowSelection(selectionMap(ids))}
          />
        }
      />

      <DeleteViewDialog
        open={deleteDialogOpen}
        viewName={savedViews.activeView?.name ?? 'this View'}
        description={WORKSPACE_VIEW_DELETE_DESCRIPTION}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => {
          setDeleteDialogOpen(false);
          void savedViews.deleteView();
        }}
      />
    </main>
  );
}

function ProjectMapLoadingSurface() {
  return (
    <output
      className="flex h-96 w-full flex-col items-center justify-center gap-3 border-b bg-muted/30 text-sm text-muted-foreground"
      aria-live="polite"
    >
      <Loader2 className="size-5 animate-spin" aria-hidden="true" />
      <span>Loading project map…</span>
    </output>
  );
}
